// src/components/resources/RelatedResources.js
// Related content section for article/case study pages

import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Clock } from 'lucide-react';
import './styles/related-resources.css';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const RelatedCard = ({ item, type = 'article' }) => {
  const linkPrefix = type === 'caseStudy' ? '/case-studies/' : '/articles/';
  const linkTo = `${linkPrefix}${item.slug}`;

  return (
    <motion.article className="related-card" variants={itemVariants}>
      <Link to={linkTo} className="related-card__link">
        {/* Image */}
        {item.heroImage?.url && (
          <div className="related-card__image-wrap">
            <img
              src={`${item.heroImage.url}?w=400&h=225&fit=fill&q=80&fm=webp`}
              alt={item.heroImage.alt || item.title}
              className="related-card__image"
              loading="lazy"
            />
            <div className="related-card__overlay">
              <ArrowUpRight size={20} />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="related-card__content">
          <h4 className="related-card__title">{item.title}</h4>

          {/* Meta */}
          <div className="related-card__meta">
            {item.readingTime && (
              <span className="related-card__time">
                <Clock size={12} />
                {item.readingTime}
              </span>
            )}
            {item.clientName && (
              <span className="related-card__client">{item.clientName}</span>
            )}
            {item.category && (
              <span className="related-card__category">{item.category}</span>
            )}
          </div>
        </div>
      </Link>
    </motion.article>
  );
};

const RelatedResources = ({ items = [], type = 'article', title = 'Keep exploring' }) => {
  const prefersReducedMotion = useReducedMotion();

  if (!items.length) return null;

  return (
    <section className="related-resources">
      <div className="related-resources__container">
        <h3 className="related-resources__heading">{title}</h3>

        <motion.div
          className="related-resources__grid"
          variants={prefersReducedMotion ? {} : containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
        >
          {items.slice(0, 3).map((item) => (
            <RelatedCard key={item.id || item.slug} item={item} type={type} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default RelatedResources;
